import styled, { css } from "styled-components";
import { useDesignTokens } from "@boi/react-design-tokens";
import { type CoreButton } from "@boi/components-core";

type ButtonProps = {
  $variant: CoreButton.Variant;
  $isLoading?: boolean;
  $fullWidth?: boolean;
};

export const ButtonBase = styled.button<ButtonProps>`
  ${({ $variant, $isLoading, $fullWidth }) => {
    const { tokens } = useDesignTokens();
    const { button } = tokens;

    return [
      css`
        all: unset;
        position: relative;
        display: grid;
        align-items: start;
        height: ${button.height};
        width: ${$fullWidth ? "100%" : "auto"};
        min-width: ${button.minWidth};
        cursor: pointer;

        &:disabled {
          cursor: not-allowed;
          opacity: ${button.opacity.disabled};
        }

        &[aria-disabled]:not([aria-disabled="false"]) {
          cursor: not-allowed;
        }
      `,
      !$isLoading &&
        css`
          &:not(:disabled):focus-visible {
            border-radius: ${button.topLayer.stroke.radius};
            outline-width: ${button.focusRing.width};
            outline-style: ${button.focusRing.style};
            outline-color: ${button.focusRing.color[$variant]};
            outline-offset: ${button.focusRing.offset};
          }
        `,
    ];
  }}
`;

type TopLayerProps = {
  $variant: CoreButton.Variant;
  $isLoading?: boolean;
};

export const TopLayer = styled.div<TopLayerProps>`
  ${({ $variant, $isLoading }) => {
    const { tokens } = useDesignTokens();
    const { button } = tokens;

    return [
      css`
        position: relative;
        overflow: hidden;
        color: ${button.topLayer.color[$variant].base};
        background: ${button.topLayer.backgroundColor[$variant].base};
        height: ${button.topLayer.height[$variant]};
        padding-left: ${button.topLayer.padding[$variant].left};
        padding-right: ${button.topLayer.padding[$variant].right};
        border-width: ${button.topLayer.stroke.width};
        border-style: ${button.topLayer.stroke.style};
        border-color: ${button.topLayer.stroke.color[$variant].base};
        border-radius: ${button.topLayer.stroke.radius};
        user-select: none;

        transform: translateY(0px);
        transition: transform ${button.topLayer.animationDuration}ms;

        ${ButtonBase}:not(:disabled):hover && {
          color: ${button.topLayer.color[$variant].hover};
          background: ${button.topLayer.backgroundColor[$variant].hover};
          transform: translateY(${button.topLayer.translateY[$variant].hover});
        }

        ${ButtonBase}:not(:disabled):active && {
          color: ${button.topLayer.color[$variant].active};
          background: ${button.topLayer.backgroundColor[$variant].active};
          transform: translateY(${button.topLayer.translateY[$variant].active});
        }
      `,
      $variant === "secondary" &&
        css`
          ${ButtonBase}:not(:disabled):hover && {
            border-color: ${button.topLayer.stroke.color[$variant].hover};
          }

          ${ButtonBase}:not(:disabled):active && {
            border-color: ${button.topLayer.stroke.color[$variant].active};
          }
        `,
      $isLoading &&
        css`
          color: ${button.topLayer.color[$variant].active};
          border-color: ${button.topLayer.stroke.color[$variant].active};
          background: ${button.topLayer.backgroundColor[$variant].active};
          transform: translateY(${button.topLayer.translateY[$variant].active});

          ${ButtonBase}:not(:disabled):hover && {
            color: ${button.topLayer.color[$variant].active};
            border-color: ${button.topLayer.stroke.color[$variant].active};
            background: ${button.topLayer.backgroundColor[$variant].active};
            transform: translateY(
              ${button.topLayer.translateY[$variant].active}
            );
          }

          ${ButtonBase}:not(:disabled):active && {
            color: ${button.topLayer.color[$variant].active};
            border-color: ${button.topLayer.stroke.color[$variant].active};
            background: ${button.topLayer.backgroundColor[$variant].active};
            transform: translateY(
              ${button.topLayer.translateY[$variant].active}
            );
          }

          ${ButtonBase}:not(:disabled):focus-visible && {
            border-radius: ${button.topLayer.stroke.radius};
            outline-width: ${button.focusRing.width};
            outline-style: ${button.focusRing.style};
            outline-color: ${button.focusRing.color[$variant]};
            outline-offset: ${button.focusRing.offset};
          }
        `,
    ];
  }}
`;

type BottomLayerProps = {
  $variant: CoreButton.Variant;
  $isLoading?: boolean;
};

export const BottomLayer = styled.div<BottomLayerProps>`
  ${({ $variant, $isLoading }) => {
    const { tokens } = useDesignTokens();
    const { button } = tokens;
    const { shadow } = button.bottomLayer;

    return [
      css`
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: ${button.bottomLayer.height};
        border-radius: ${button.bottomLayer.radius};
        background: ${button.bottomLayer.backgroundColor[$variant].base};
        box-shadow: 0px ${shadow.blur} ${shadow.offsetY} ${shadow.color};

        ${ButtonBase}:not(:disabled):hover && {
          background: ${button.bottomLayer.backgroundColor[$variant].hover};
        }

        ${ButtonBase}:not(:disabled):active && {
          background: ${button.bottomLayer.backgroundColor[$variant].active};
          box-shadow: none;
        }
      `,
      $isLoading &&
        css`
          background: ${button.bottomLayer.backgroundColor[$variant].active};
          box-shadow: none;

          ${ButtonBase}:not(:disabled):hover && {
            background: ${button.bottomLayer.backgroundColor[$variant].active};
          }

          ${ButtonBase}:not(:disabled):active && {
            background: ${button.bottomLayer.backgroundColor[$variant].active};
          }
        `,
    ];
  }}
`;
